import {
  ArrowLeft,
  Brain,
  TrendingUp,
  Heart,
  Moon,
  Activity,
  Wind,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import welcomeBg from "../../assets/homebg.png";
import fitnessService, { StressScoreData } from "../../services/FitnessService";

const StressScoreDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stressData, setStressData] = useState<StressScoreData | null>(null);
  const [loading, setLoading] = useState(true);

  // Get selected date from navigation state or use today
  const selectedDate =
    location.state?.selectedDate || new Date().toISOString().split("T")[0];

  useEffect(() => {
    const fetchStressData = async () => {
      try {
        setLoading(true);
        const result = await fitnessService.getStressScore(selectedDate);
        setStressData(result);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchStressData();
  }, [selectedDate]);

  // Format stress score - round to 1 decimal place, remove if it's .0
  const rawStressScore = stressData?.stress_score || 0;
  const stressScore = Math.round(rawStressScore * 10) / 10; // Round to 1 decimal
  
  const getStressLevel = () => {
    const level = stressData?.stress_level;
    if (level === "Low" || stressScore < 40) {
      return { level: "Low", color: "text-green-600", bgColor: "bg-green-100" };
    }
    if (level === "Moderate" || stressScore < 70) {
      return {
        level: "Moderate",
        color: "text-orange-600",
        bgColor: "bg-orange-100",
      };
    }
    return { level: "High", color: "text-red-600", bgColor: "bg-red-100" };
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              navigate("/glucera/fitness", {
                state: {
                  selectedDate: selectedDate,
                  selectedDateObj: location.state?.selectedDateObj,
                },
              })
            }
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="text-center">
            <h1 className="text-title2 font-bold text-foreground">Stress Score</h1>
          </div>
          <div className="w-10" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-title3 text-muted-foreground">Loading...</div>
          </div>
        ) : (
          <>
            {/* Current Stress Level Card */}
            <Card className="rounded-2xl border-none bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-6 shadow-lg dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
              <div className="mb-4 flex items-center gap-2">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-pink-500">
                  <Brain className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-caption1 font-medium text-gray-500 dark:text-gray-400">
                    Current Stress Level
                  </p>
                  <p className="text-subhead font-bold text-gray-900 dark:text-white">
                    {getStressLevel().level}
                  </p>
                </div>
              </div>

              {/* Large Score Display */}
              <div className="mb-4 text-center">
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-6xl font-bold text-gray-900 dark:text-white">
                    {stressScore % 1 === 0 ? Math.round(stressScore) : stressScore.toFixed(1)}
                  </span>
                  <span className="text-title1 font-semibold text-gray-500 dark:text-gray-400">
                    / 100
                  </span>
                </div>
              </div>

              {/* Stress Level Indicator */}
              <div className="mt-4">
                <div className="mb-2 flex justify-between text-caption1 font-bold">
                  <span className="text-emerald-600 dark:text-emerald-400">
                    Low
                  </span>
                  <span className="text-amber-600 dark:text-amber-400">
                    Moderate
                  </span>
                  <span className="text-rose-600 dark:text-rose-400">High</span>
                </div>
                <div className="relative h-4 overflow-hidden rounded-full bg-white/80 shadow-inner dark:bg-gray-700">
                  <div className="absolute inset-0 flex">
                    <div className="w-1/3 bg-gradient-to-r from-emerald-300 to-emerald-400" />
                    <div className="w-1/3 bg-gradient-to-r from-amber-300 to-amber-400" />
                    <div className="w-1/3 bg-gradient-to-r from-rose-300 to-rose-400" />
                  </div>
                  <div
                    className="absolute top-0 h-full w-1.5 rounded-full bg-gray-800 shadow-lg dark:bg-white"
                    style={{
                      left: `calc(${stressScore}% - 3px)`,
                      boxShadow: "0 0 10px rgba(0,0,0,0.4)",
                    }}
                  />
                </div>
                <div className="mt-2 text-center">
                  <span
                    className={`inline-block rounded-full px-4 py-1.5 text-caption1 font-bold ${
                      getStressLevel().bgColor
                    } ${getStressLevel().color}`}
                  >
                    {getStressLevel().level} Stress
                  </span>
                </div>
              </div>
            </Card>

            {/* Stress Levels Info */}
            <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
              <h3 className="mb-4 text-callout font-semibold text-foreground">
                Stress Levels
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-lg bg-green-50 p-3 dark:bg-green-950">
                  <span className="text-subhead font-medium text-foreground">
                    Low Stress
                  </span>
                  <span className="text-subhead text-muted-foreground">0 - 39</span>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-orange-50 p-3 dark:bg-orange-950">
                  <span className="text-subhead font-medium text-foreground">
                    Moderate Stress
                  </span>
                  <span className="text-subhead text-muted-foreground">
                    40 - 69
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-red-50 p-3 dark:bg-red-950">
                  <span className="text-subhead font-medium text-foreground">
                    High Stress
                  </span>
                  <span className="text-subhead text-muted-foreground">
                    70 - 100
                  </span>
                </div>
              </div>
            </Card>

            {/* Contributing Factors */}
            {stressData?.contributing_factors && (
              <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
                <h3 className="mb-4 text-callout font-semibold text-foreground">
                  Contributing Factors
                </h3>
                <div className="space-y-3">
                  {/* HRV */}
                  <div className="flex items-center justify-between rounded-lg bg-muted/30 p-3">
                    <div className="flex items-center gap-3">
                      <Activity className="h-5 w-5 text-pink-500" />
                      <span className="text-subhead font-medium text-foreground">
                        Heart Rate Variability
                      </span>
                    </div>
                    <span className="text-subhead font-bold text-foreground">
                      {stressData.contributing_factors.hrv}
                    </span>
                  </div>

                  {/* Heart Rate */}
                  <div className="flex items-center justify-between rounded-lg bg-muted/30 p-3">
                    <div className="flex items-center gap-3">
                      <Heart className="h-5 w-5 text-red-500" />
                      <span className="text-subhead font-medium text-foreground">
                        Heart Rate
                      </span>
                    </div>
                    <span className="text-subhead font-bold text-foreground">
                      {stressData.contributing_factors.heart_rate} BPM
                    </span>
                  </div>

                  {/* Sleep Quality */}
                  <div className="flex items-center justify-between rounded-lg bg-muted/30 p-3">
                    <div className="flex items-center gap-3">
                      <Moon className="h-5 w-5 text-blue-500" />
                      <span className="text-subhead font-medium text-foreground">
                        Sleep Quality
                      </span>
                    </div>
                    <span className="text-subhead font-bold text-foreground">
                      {stressData.contributing_factors.sleep_quality}
                    </span>
                  </div>

                  {/* Activity Level */}
                  <div className="flex items-center justify-between rounded-lg bg-muted/30 p-3">
                    <div className="flex items-center gap-3">
                      <Activity className="h-5 w-5 text-purple-500" />
                      <span className="text-subhead font-medium text-foreground">
                        Activity Level
                      </span>
                    </div>
                    <span className="text-subhead font-bold text-foreground">
                      {stressData.contributing_factors.activity_level}
                    </span>
                  </div>
                </div>
              </Card>
            )}

            {/* How is Stress Calculated */}
            <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
              <div className="mb-4 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-blue-500">
                  <span className="text-subhead font-semibold text-blue-500">?</span>
                </div>
                <h3 className="text-callout font-semibold text-foreground">
                  How is Stress Calculated?
                </h3>
              </div>
              <p className="mb-4 text-subhead text-muted-foreground">
                Your stress score is calculated using a combination of{" "}
                <span className="font-semibold text-foreground">
                  physiological signals
                </span>{" "}
                collected from your wearable device during sleep and throughout
                the day.
              </p>
              <p className="mb-4 text-subhead text-muted-foreground">
                The algorithm analyzes multiple factors with different weights
                based on their impact on stress:
              </p>

              <div className="space-y-4">
                {/* Heart Rate */}
                <div className="flex gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-red-100 dark:bg-red-950">
                    <Heart className="h-5 w-5 text-red-500" />
                  </div>
                  <div>
                    <h4 className="text-subhead font-semibold text-foreground">
                      Heart Rate
                    </h4>
                    <p className="text-caption1 text-muted-foreground">
                      Elevated resting heart rate often indicates higher stress
                      levels
                    </p>
                  </div>
                </div>

                {/* HRV */}
                <div className="flex gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-pink-100 dark:bg-pink-950">
                    <Activity className="h-5 w-5 text-pink-500" />
                  </div>
                  <div>
                    <h4 className="text-subhead font-semibold text-foreground">
                      Heart Rate Variability (HRV)
                    </h4>
                    <p className="text-caption1 text-muted-foreground">
                      Lower HRV suggests your body is under stress and has
                      reduced recovery capacity
                    </p>
                  </div>
                </div>

                {/* Sleep Quality */}
                <div className="flex gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-950">
                    <Moon className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <h4 className="text-subhead font-semibold text-foreground">
                      Sleep Quality
                    </h4>
                    <p className="text-caption1 text-muted-foreground">
                      Poor or insufficient sleep directly contributes to stress
                      accumulation
                    </p>
                  </div>
                </div>

                {/* Breathing Rate */}
                <div className="flex gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-green-100 dark:bg-green-950">
                    <Wind className="h-5 w-5 text-green-500" />
                  </div>
                  <div>
                    <h4 className="text-subhead font-semibold text-foreground">
                      Breathing Rate
                    </h4>
                    <p className="text-caption1 text-muted-foreground">
                      Elevated breathing rate during sleep can indicate stress
                      or anxiety
                    </p>
                  </div>
                </div>

                {/* Skin Temperature */}
                <div className="flex gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-950">
                    <TrendingUp className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <h4 className="text-subhead font-semibold text-foreground">
                      Skin Temperature
                    </h4>
                    <p className="text-caption1 text-muted-foreground">
                      Variations from your baseline can signal stress responses
                    </p>
                  </div>
                </div>

                {/* Active Zone Minutes */}
                <div className="flex gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-950">
                    <Activity className="h-5 w-5 text-purple-500" />
                  </div>
                  <div>
                    <h4 className="text-subhead font-semibold text-foreground">
                      Active Zone Minutes
                    </h4>
                    <p className="text-caption1 text-muted-foreground">
                      Regular physical activity helps reduce stress levels
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Tips to Reduce Stress */}
            <Card className="rounded-2xl border border-border bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-5 shadow-lg dark:from-blue-950 dark:via-indigo-950 dark:to-purple-950">
              <div className="mb-4 flex items-center gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-pink-500">
                  <Brain className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-callout font-bold text-foreground">
                  {stressData?.recommendations
                    ? "Personalized Recommendations"
                    : "Tips to Reduce Stress"}
                </h3>
              </div>
              <div className="space-y-2.5">
                {stressData?.recommendations &&
                stressData.recommendations.length > 0 ? (
                  stressData.recommendations.map((tip, index) => (
                    <div
                      key={index}
                      className="flex gap-3 rounded-xl border border-border bg-white/60 p-3.5 shadow-sm dark:bg-gray-800/40"
                    >
                      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-emerald-500">
                        <span className="text-caption1 font-bold text-white">✓</span>
                      </div>
                      <p className="text-subhead font-medium leading-relaxed text-foreground">
                        {tip}
                      </p>
                    </div>
                  ))
                ) : (
                  <>
                    <div className="flex gap-3 rounded-xl border border-border bg-white/60 p-3.5 shadow-sm dark:bg-gray-800/40">
                      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-emerald-500">
                        <span className="text-caption1 font-bold text-white">✓</span>
                      </div>
                      <p className="text-subhead font-medium leading-relaxed text-foreground">
                        Practice deep breathing exercises for 5-10 minutes daily
                      </p>
                    </div>
                    <div className="flex gap-3 rounded-xl border border-border bg-white/60 p-3.5 shadow-sm dark:bg-gray-800/40">
                      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-emerald-500">
                        <span className="text-caption1 font-bold text-white">✓</span>
                      </div>
                      <p className="text-subhead font-medium leading-relaxed text-foreground">
                        Maintain a consistent sleep schedule (7-9 hours)
                      </p>
                    </div>
                    <div className="flex gap-3 rounded-xl border border-border bg-white/60 p-3.5 shadow-sm dark:bg-gray-800/40">
                      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-emerald-500">
                        <span className="text-caption1 font-bold text-white">✓</span>
                      </div>
                      <p className="text-subhead font-medium leading-relaxed text-foreground">
                        Get at least 150 minutes of moderate exercise per week
                      </p>
                    </div>
                    <div className="flex gap-3 rounded-xl border border-border bg-white/60 p-3.5 shadow-sm dark:bg-gray-800/40">
                      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-emerald-500">
                        <span className="text-caption1 font-bold text-white">✓</span>
                      </div>
                      <p className="text-subhead font-medium leading-relaxed text-foreground">
                        Limit caffeine and alcohol intake
                      </p>
                    </div>
                    <div className="flex gap-3 rounded-xl border border-border bg-white/60 p-3.5 shadow-sm dark:bg-gray-800/40">
                      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-emerald-500">
                        <span className="text-caption1 font-bold text-white">✓</span>
                      </div>
                      <p className="text-subhead font-medium leading-relaxed text-foreground">
                        Try meditation or mindfulness practices
                      </p>
                    </div>
                  </>
                )}
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default StressScoreDetail;
